import moment from 'moment-timezone';
import {
  STimeConverter, STripTypeEnum, SBookingRequestService, SReservationService, SBookingStopTypeEnum,
} from '@slg/web-customer-shared';
import { bookingDetails, type BookingState } from './bookingDetails/bookingDetailsState';

export default {
  namespaced: true,
  state: {
    ...bookingDetails,
  } as BookingState,
  mutations: {
    setArrivalPlace(state: BookingState, data: Record<string, any>) {
      state.arrivalPlace.address = data.address;
      state.arrivalPlace.poiName = data.poiName;
      state.arrivalPlace.poiStopName = data.poiStopName;
      state.arrivalPlace.lat = data.lat;
      state.arrivalPlace.lon = data.lon;
      state.arrivalPlace.type = data.type;
      state.arrivalPlace.isPoi = data.isPoi;
      state.arrivalPlace.poiStopId = data.poiStopId;
      state.arrivalPlace.stopId = data.stopId;
      state.arrivalPlace.error = data.error;
    },
    clearArrivalPlace(state: BookingState) {
      const keys = Object.keys(state.arrivalPlace) as Array<keyof typeof state.arrivalPlace>;
      keys.forEach((key) => {
        (state.arrivalPlace as Record<string, any>)[key] = '';
      });
    },
    setDeparturePlace(state: BookingState, data: Record<string, any>) {
      state.departurePlace.address = data.address;
      state.departurePlace.poiName = data.poiName;
      state.departurePlace.poiStopName = data.poiStopName;
      state.departurePlace.lat = data.lat;
      state.departurePlace.lon = data.lon;
      state.departurePlace.type = data.type;
      state.departurePlace.isPoi = data.isPoi;
      state.departurePlace.poiStopId = data.poiStopId;
      state.departurePlace.stopId = data.stopId;
      state.departurePlace.error = data.error;
    },
    clearDeparturePlace(state: BookingState) {
      const keys = Object.keys(state.departurePlace) as Array<keyof typeof state.departurePlace>;
      keys.forEach((key) => {
        (state.departurePlace as Record<string, any>)[key] = '';
      });
    },
    setRideType(state: BookingState, rideType: string) {
      state.rideType = rideType;
    },
    populateBookingDetails(state: BookingState, details: Record<string, any>) {
      const keys = Object.keys(state.bookingDetails);
      keys.forEach((key) => {
        (state.bookingDetails as Record<string, any>)[key] = details[key];
      });
    },
    clearBookingDetails(state: BookingState) {
      state.bookingDetails = { ...bookingDetails.bookingDetails };
    },
    setTripType(state: BookingState, tripType: string) {
      state.tripType = tripType;
    },
    setAdults(state: BookingState, number: number) {
      state.adults = number;
    },
    setChildren(state: BookingState, number: number) {
      state.children = number;
    },
    setFeatures(state: BookingState, selectedFeatures: any[]) {
      state.selectedFeatures = selectedFeatures;
    },
    setFeatureAmount(state: BookingState, data: { vehicleFeatureId: string; amount: number }) {
      const targetFeatureIndex = state.selectedFeatures.findIndex(({ vehicleFeatureId }) => vehicleFeatureId === data.vehicleFeatureId);
      state.selectedFeatures[targetFeatureIndex].amount = data.amount;
    },
    setAnimalsTransportation(state: BookingState, animalsTransportation: boolean) {
      state.animalsTransportation = animalsTransportation;
    },
    setVehicleCategoryId(state: BookingState, vehicleCategoryId: string) {
      state.vehicleCategoryId = vehicleCategoryId;
    },
    setVehicleCategoryLocalizedName(state: BookingState, localizedName: unknown) {
      state.vehicleCategoryLocalizedName = localizedName as string;
    },
    setOutwardDate(state: BookingState, data: { type: string; time: string; date: string }) {
      state.outwardDate.type = data.type;
      state.outwardDate.time = data.time;
      state.outwardDate.date = data.date;
    },
    setReturnDate(state: BookingState, data: { type: string; time: string; date: string }) {
      state.returnDate.type = data.type;
      state.returnDate.time = data.time;
      state.returnDate.date = data.date;
    },
    populateRideList(state: BookingState, rideList: any[]) {
      state.rideList = rideList;
    },
    setCommentForOutward(state: BookingState, comment: string) {
      state.checkoutInfo.commentForOutward = comment;
    },
    setCommentForReturn(state: BookingState, comment: string) {
      state.checkoutInfo.commentForReturn = comment;
    },
    setPaymentType(state: BookingState, type: string) {
      state.checkoutInfo.paymentType = type;
    },
    setPaymentMethod(state: BookingState, paymentMethod: string | null) {
      state.checkoutInfo.paymentMethod = paymentMethod;
    },
    setFlightDetails(state: BookingState, details: string) {
      state.checkoutInfo.flightDetails = details;
    },
    setPassengerPhone(state: BookingState, phone: string) {
      state.checkoutInfo.passengerDetails.phone = phone;
    },
    setPassengerFirstName(state: BookingState, firstName: string) {
      state.checkoutInfo.passengerDetails.firstName = firstName;
    },
    setPassengerLastName(state: BookingState, lastName: string) {
      state.checkoutInfo.passengerDetails.lastName = lastName;
    },
    setPassengerEmail(state: BookingState, email: string) {
      state.checkoutInfo.passengerDetails.email = email;
    },
    setAnotherPassenger(state: BookingState, isAnotherPassenger: boolean) {
      state.checkoutInfo.passengerDetails.isAnotherPassenger = isAnotherPassenger;
      state.checkoutInfo.passengerDetails.phone = '';
      state.checkoutInfo.passengerDetails.firstName = '';
      state.checkoutInfo.passengerDetails.lastName = '';
      state.checkoutInfo.passengerDetails.email = '';
    },
    populateTemporaryReservation({ checkoutInfo }: BookingState, reservationDetails: Record<string, any>) {
      checkoutInfo.temporaryReservation.bookingRequestId = reservationDetails.bookingRequestId;
      checkoutInfo.temporaryReservation.reservationId = reservationDetails.reservationId;
      checkoutInfo.temporaryReservation.remainingTime = Math.round((reservationDetails.reservedTill - reservationDetails.now) / 1000);
      checkoutInfo.temporaryReservation.reservedTill = reservationDetails.reservedTill;
    },
    setRemainingTime({ checkoutInfo }: BookingState, remainingTime: number) {
      checkoutInfo.temporaryReservation.remainingTime = remainingTime;
    },
    setIgnoreRemainingTime({ checkoutInfo }: BookingState, ignoreRemainingTime: boolean) {
      checkoutInfo.temporaryReservation.ignoreRemainingTime = ignoreRemainingTime;
    },
    setLoggedInPassengerDetails({ checkoutInfo }: BookingState, passengerDetails: Record<string, any>) {
      checkoutInfo.passengerDetails.isAnotherPassenger = false;
      checkoutInfo.passengerDetails.phone = passengerDetails.phone;
      checkoutInfo.passengerDetails.firstName = passengerDetails.firstName;
      checkoutInfo.passengerDetails.lastName = passengerDetails.lastName;
      checkoutInfo.passengerDetails.email = passengerDetails.email;
    },
    setUpdatedPriceByNightCards({ bookingDetails: details }: BookingState, price: Record<string, any>) {
      details.originalOutwardFeaturePrice = Number(price.originalOutwardFeaturePrice);
      details.originalOutwardPrice = Number(price.originalOutwardPrice);
      details.originalReturnFeaturePrice = Number(price.originalReturnFeaturePrice);
      details.originalReturnPrice = Number(price.originalReturnPrice);
    },
    clearDates({ outwardDate, returnDate }: BookingState) {
      outwardDate.type = '';
      outwardDate.time = '';
      outwardDate.date = '';
      returnDate.type = '';
      returnDate.time = '';
      returnDate.date = '';
    },
    clearOptions(state: BookingState) {
      state.animalsTransportation = false;
      state.rideType = '';
      state.tripType = 'ONE_WAY';
      state.vehicleCategoryId = '';
      state.adults = 1;
      state.children = 0;
      state.selectedFeatures = [];
    },
    clearTemporaryReservationDeadline(state: BookingState) {
      state.checkoutInfo.temporaryReservation.remainingTime = 0;
    },
    clearTemporaryReservation(state: BookingState) {
      state.checkoutInfo.temporaryReservation.bookingRequestId = '';
      state.checkoutInfo.temporaryReservation.reservedTill = 0;
      state.checkoutInfo.temporaryReservation.reservationId = '';
      state.checkoutInfo.temporaryReservation.remainingTime = 300;
      state.bookingDetails.bookingRequestId = '';
    },
    clearBookingState(state: BookingState) {
      Object.assign(state, JSON.parse(JSON.stringify(bookingDetails)));
    },
  },
  actions: {
    async setBookingDetails({ commit }: { commit: (type: string, payload?: unknown) => void }, bookingCreateModel: any) {
      const response = await SBookingRequestService.bookingCreate(bookingCreateModel);
      commit('populateBookingDetails', response.data);
    },
    async setTemporaryReservation({ commit }: { commit: (type: string, payload?: unknown) => void }, bookingRequestId: string) {
      const now = new Date().valueOf();
      const response = await SReservationService.reservationCreate(bookingRequestId);
      (response.data as any).now = now;
      commit('populateTemporaryReservation', response.data);
    },
    async cancelTemporaryReservation({ commit, state }: { commit: (type: string, payload?: unknown) => void; state: BookingState }) {
      if (state.checkoutInfo.temporaryReservation.reservationId) {
        try {
          await SReservationService.reservationCancel(state.checkoutInfo.temporaryReservation.bookingRequestId);
        } catch (_error) {
          // do nothing
        } finally {
          commit('clearTemporaryReservation');
        }
      }
    },
  },
  getters: {
    getBookingForm: (state: BookingState) => state,
    getBookingDetails: (state: BookingState) => state.bookingDetails,
    getAnimalsTransportation: (state: BookingState) => state.animalsTransportation,
    getReturnDate: (state: BookingState) => state.returnDate.date,
    getReturnState: (state: BookingState) => state.returnDate,
    getReturnTime: (state: BookingState) => state.returnDate.time,
    getReturnType: (state: BookingState) => state.returnDate.type,
    getOutwardDate: (state: BookingState) => state.outwardDate.date,
    getOutwardState: (state: BookingState) => state.outwardDate,
    getOutwardTime: (state: BookingState) => state.outwardDate.time,
    getOutwardType: (state: BookingState) => state.outwardDate.type,
    getAdults: (state: BookingState) => state.adults,
    getChildren: (state: BookingState) => state.children,
    atLeastOnePassengerSelected: (state: BookingState) => (state.adults + state.children) > 0,
    getVehicleCategoryId: (state: BookingState) => state.vehicleCategoryId,
    getVehicleCategoryLocalizedName: (state: BookingState) => state.vehicleCategoryLocalizedName,
    getSelectedFeatures: (state: BookingState) => state.selectedFeatures,
    getRideType: (state: BookingState) => state.rideType,
    getTripType: (state: BookingState) => state.tripType,
    isRoundTrip: (state: BookingState) => state.tripType === STripTypeEnum.ROUND_TRIP,
    getArrivalPlace: (state: BookingState) => state.arrivalPlace,
    isArrivalPlaceFilled: ({ arrivalPlace }: BookingState) => arrivalPlace.address && arrivalPlace.lat && arrivalPlace.lon,
    getDeparturePlace: (state: BookingState) => state.departurePlace,
    isDeparturePlaceFilled: ({ departurePlace }: BookingState) => departurePlace.address && departurePlace.lat && departurePlace.lon,
    isRideToOrFromAirport: ({ departurePlace, arrivalPlace }: BookingState) => ((departurePlace.isPoi && departurePlace.type === 'airport')
      || (arrivalPlace.isPoi && arrivalPlace.type === 'airport')),
    getStops: (state: BookingState) => [state.departurePlace, state.arrivalPlace],
    getSelectedRides: (state: BookingState, getters: Record<string, any>) => ([
      {
        title: getters.isRoundTrip ? 'bookingForm.outwardTime' : 'bookingForm.oneWayOutwardTime',
        priceTitle: getters.isRoundTrip ? 'bookingForm.outwardRidePrice' : 'bookingForm.oneWayOutwardRidePrice',
        featurePriceTitle: getters.isRoundTrip ? 'bookingForm.outwardRideFeatures' : 'bookingForm.oneWayOutwardRideFeatures',
        outward: state.departurePlace,
        arrival: state.arrivalPlace,
        rideType: state.rideType,
        timeZone: moment.tz.guess(),
        isDepartureExactTime: state.outwardDate.type === SBookingStopTypeEnum.DEPARTURE_TIME,
        dateState: { time: STimeConverter.convertStringToMilliseconds(state.outwardDate.date, state.outwardDate.time) },
        bookingStopType: state.outwardDate.type,
        tripDuration: state.bookingDetails.outwardTimeInMinutes,
        timeInMinutes: (state.bookingDetails.outwardTimeInMinutes || 0) * 60 * 1000,
        distanceKm: state.bookingDetails.outwardDistanceKm,
        maxDetourTime: state.bookingDetails.outwardMaxDetourTimeInMinutes,
        featurePrice: state.bookingDetails.originalOutwardFeaturePrice,
        ridePrice: state.bookingDetails.originalOutwardPrice,
      },
      ...(getters.isRoundTrip
        ? [{
          title: 'bookingForm.returnTime',
          priceTitle: 'bookingForm.returnRidePrice',
          featurePriceTitle: 'bookingForm.returnRideFeatures',
          outward: state.arrivalPlace,
          arrival: state.departurePlace,
          rideType: state.rideType,
          timeZone: moment.tz.guess(),
          isDepartureExactTime: state.returnDate.type === SBookingStopTypeEnum.DEPARTURE_TIME,
          dateState: { time: STimeConverter.convertStringToMilliseconds(state.returnDate.date, state.returnDate.time) },
          bookingStopType: state.returnDate.type,
          tripDuration: state.bookingDetails.returnTimeInMinutes,
          timeInMinutes: (state.bookingDetails.returnTimeInMinutes || 0) * 60 * 1000,
          distanceKm: state.bookingDetails.returnDistanceKm,
          maxDetourTime: state.bookingDetails.returnMaxDetourTimeInMinutes,
          featurePrice: state.bookingDetails.originalReturnFeaturePrice,
          ridePrice: state.bookingDetails.originalReturnPrice,
        }]
        : []
      ),
    ]),
    isOutwardVehicleFound: (state: BookingState) => (state as any).isOutwardVehicleFound,
    isReturnVehicleFound: (state: BookingState) => (state as any).isReturnVehicleFound,
    getPaymentType: (state: BookingState) => state.checkoutInfo.paymentType,
    getPaymentMethod: (state: BookingState) => state.checkoutInfo.paymentMethod,
    getCheckoutInfo: (state: BookingState) => state.checkoutInfo,
    getFlightDetails: (state: BookingState) => state.checkoutInfo.flightDetails,
    getCommentForOutward: (state: BookingState) => state.checkoutInfo.commentForOutward,
    getCommentForReturn: (state: BookingState) => state.checkoutInfo.commentForReturn,
    getPassengerPhone: (state: BookingState) => state.checkoutInfo.passengerDetails.phone,
    getPassengerFirstName: (state: BookingState) => state.checkoutInfo.passengerDetails.firstName,
    getPassengerLastName: (state: BookingState) => state.checkoutInfo.passengerDetails.lastName,
    getPassengerEmail: (state: BookingState) => state.checkoutInfo.passengerDetails.email,
    isAnotherPassenger: (state: BookingState) => state.checkoutInfo.passengerDetails.isAnotherPassenger,
    getTemporaryReservationDeadline: (state: BookingState) => state.checkoutInfo.temporaryReservation.reservedTill,
    getTemporaryReservationRemainingTime: (state: BookingState) => state.checkoutInfo.temporaryReservation.remainingTime,
    getIgnoreRemainingTime: (state: BookingState) => state.checkoutInfo.temporaryReservation.ignoreRemainingTime,
    getReservationId: (state: BookingState) => state.checkoutInfo.temporaryReservation.reservationId,
    getBookingRequestId: (state: BookingState) => state.bookingDetails.bookingRequestId,
    getIsFreeTrip: (state: BookingState) => (state.tripType === STripTypeEnum.ROUND_TRIP
      ? (!Number(state.bookingDetails.originalOutwardPrice) && !Number(state.bookingDetails.originalReturnPrice))
      : !Number(state.bookingDetails.originalOutwardPrice)),
    getRejectReasonCode: (state: BookingState) => state.bookingDetails.rejectReasonCode,
  },
};
